import { useCallback } from "react";
import { useNavigate } from "react-router";
import navHelper from "services/navHelper";
import {
    getAgentScoringRulesPath,
    getAgentScoringRuleCreatePath,
    getAgentScoringRuleEditorPath,
} from "util/routeUtils";
import { shouldRequestHostNavigation } from "./agentScoringRuleNavigation";

/**
 * Navigation for the Agent Scoring Rules views.
 *
 * These views are embedded (as a nested iframe) inside the programs-definitions-ui
 * Program Editor, which is itself an iframe-coordinator client of web-directory.
 * When nested we must not navigate inside our own iframe (that would render the
 * rule editor cramped under the Program Editor tab). Instead we ask the host to
 * navigate via `navHelper` — the same `embeddingService.requestNavigation` contract
 * quality-forms already uses for evaluation/calibration navigation. The host
 * (programs-definitions-ui) relays the request up to web-directory, which changes
 * the top-level route so the editor is shown as its own full page.
 *
 * Nested iframe detection lives in `shouldRequestHostNavigation`.
 */
export function useAgentScoringRuleNavigation(programId: string) {
    const navigate = useNavigate();

    // Nested embedding → host navigation; otherwise route in-app.
    const navigateToPath = useCallback(
        (path: string) => {
            if (shouldRequestHostNavigation()) {
                navHelper.navigateTo(path);
            } else {
                navigate(path);
            }
        },
        [navigate]
    );

    const openCreate = useCallback(() => {
        navigateToPath(getAgentScoringRuleCreatePath(programId));
    }, [navigateToPath, programId]);

    const openEditor = useCallback(
        (ruleId: string) => {
            navigateToPath(getAgentScoringRuleEditorPath(programId, ruleId));
        },
        [navigateToPath, programId]
    );

    const openList = useCallback(() => {
        navigateToPath(getAgentScoringRulesPath(programId));
    }, [navigateToPath, programId]);

    return { openCreate, openEditor, openList };
}

export default useAgentScoringRuleNavigation;
